/* eslint no-shadow: ["error", { "allow": ["state"] }] */
/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["state"] }] */

import axios from 'axios';
import moment from 'moment-timezone';
import globalVars from '../../globalVars';
import { PropertyException } from '../../util/exceptions';

export default function returnStoreModule() {
    return globalVars().then((data) => {
        if (!data.baseApi) {
            throw new PropertyException('baseApi');
        }
        function getDayNumber(day) {
            switch (day) {
            case 'Monday':
                return 1;
            case 'Tuesday':
                return 2;
            case 'Wednesday':
                return 3;
            case 'Thursday':
                return 4;
            case 'Friday':
                return 5;
            case 'Saturday':
                return 6;
            case 'Sunday':
                return 7;
            default:
                return false;
            }
        }

        function sortDays(week, timezone) {
            const now = moment().tz(timezone);
            const today = moment(now).format('dddd');
            const newWeek = week.sort((item1, item2) => {
                const day1 = item1.dayIndex;
                const day2 = item2.dayIndex;
                const todayDayNumber = getDayNumber(today);
                // If the today is either of the days, move it to the bottom
                if (day1 === todayDayNumber) {
                    return -1;
                }
                if (day2 === todayDayNumber) {
                    return 1;
                }
                // If both the days are earlier than today, move them to the end in order
                if (day1 < todayDayNumber && day2 < todayDayNumber) {
                    if (day1 < day2) {
                        return -1;
                    }
                    return 1;
                }
                // If just one of the days is earlier than today, move it to the end and not the other
                if (day1 < todayDayNumber) {
                    return 1;
                }
                if (day2 < todayDayNumber) {
                    return -1;
                }
                // Otherwise, if the day meets none of the conditions above, sort it as normal
                if (day1 < day2) {
                    return -1;
                }
                return 1;
            });
            return newWeek;
        }

        function shopHours(days, timezone) {
            let week = [
                {
                    closed: false,
                    day: 'Monday',
                    openTime: '',
                    closeTime: '',
                    dayIndex: 1,
                    byAppointment: false,
                    '24Hours': false
                },
                {
                    closed: false,
                    day: 'Tuesday',
                    openTime: '',
                    closeTime: '',
                    dayIndex: 2,
                    byAppointment: false,
                    '24Hours': false
                },
                {
                    closed: false,
                    day: 'Wednesday',
                    openTime: '',
                    closeTime: '',
                    dayIndex: 3,
                    byAppointment: false,
                    '24Hours': false
                },
                {
                    closed: false,
                    day: 'Thursday',
                    openTime: '',
                    closeTime: '',
                    dayIndex: 4,
                    byAppointment: false,
                    '24Hours': false
                },
                {
                    closed: false,
                    day: 'Friday',
                    openTime: '',
                    closeTime: '',
                    dayIndex: 5,
                    byAppointment: false,
                    '24Hours': false
                },
                {
                    closed: false,
                    day: 'Saturday',
                    openTime: '',
                    closeTime: '',
                    dayIndex: 6,
                    byAppointment: false,
                    '24Hours': false
                },
                {
                    closed: false,
                    day: 'Sunday',
                    openTime: '',
                    closeTime: '',
                    dayIndex: 7,
                    byAppointment: false,
                    '24Hours': false
                }
            ];
            week.map((day) => {
                const newDay = day;
                const dayInData = days.find(item => day.day === item.day);
                if (dayInData !== undefined) {
                    for (const prop in dayInData) {
                        if (Object.prototype.hasOwnProperty.call(dayInData, prop)) {
                            newDay[prop] = dayInData[prop];
                        }
                    }
                }
                return newDay;
            });
            week = sortDays(week, timezone);
            return week;
        }

        function getTimezone(shopsList) {
            const shop = shopsList.find(item => item.shopInfo.timezone !== undefined);
            return shop.shopInfo.timezone;
        }

        const state = () => ({
            shopsList: [],
            shopDetails: {},
            activeShopId: null,
            currentlySearching: false
        });

        const mutations = {
            updateShopsList(state, shopsList) {
                state.shopsList = shopsList;
            },
            updateShopDetails(state, shopDetails) {
                state.shopDetails = shopDetails;
            },
            updateActiveShopId(state, shopId) {
                state.activeShopId = shopId;
            },
            updateCurrentlySearching(state, currentlySearching) {
                state.currentlySearching = currentlySearching;
            }
        };

        const actions = {
            getShopsList({ commit, dispatch, rootState }) {
                const location = rootState.locations.selectedLocation;
                if (location.latitude && location.longitude) {
                    commit('updateCurrentlySearching', true);
                    return axios.get(`${data.baseApi}search/shops`, {
                        params: {
                            longitude: location.longitude,
                            latitude: location.latitude,
                            radius: 10,
                            carAccountsOnly: true
                        }
                    }).then((response) => {
                        let shopsList = response.data;
                        if (shopsList.length > 0) {
                            const timezone = getTimezone(shopsList);
                            shopsList = shopsList.map((shop) => {
                                const newShop = shop;
                                if (!newShop.shopInfo.timezone) {
                                    newShop.shopInfo.timezone = timezone;
                                }
                                if (newShop.shopInfo.shopHours !== undefined) {
                                    newShop.shopInfo.shopHours = shopHours(newShop.shopInfo.shopHours, timezone);
                                }
                                return newShop;
                            });
                            dispatch('locations/setTimezone', timezone, { root: true });
                        }
                        commit('updateShopsList', shopsList);
                    }).catch(() => {
                        commit('updateShopsList', null);
                    }).finally(() => {
                        commit('updateCurrentlySearching', false);
                    });
                }
                return null;
            },
            getShopDetails({ commit, rootState }, shopId) {
                return axios.get(`${data.baseApi}search/info`, {
                    params: {
                        id: shopId
                    }
                }).then((response) => {
                    const shopDetails = response.data;
                    const { timezone } = rootState.locations;
                    if (!shopDetails.timezone) {
                        shopDetails.timezone = timezone;
                    }
                    if (shopDetails.shopHours !== undefined) {
                        shopDetails.shopHours = shopHours(shopDetails.shopHours, timezone);
                    }
                    commit('updateShopDetails', shopDetails);
                });
            },
            setActiveShop({ commit }, shopId) {
                commit('updateActiveShopId', shopId);
            },
            setCurrentlySearching({ commit }, currentlySearching) {
                commit('updateCurrentlySearching', currentlySearching);
            }
        };

        const getters = {
            shopsList: state => state.shopsList,
            shopDetails: state => state.shopDetails,
            activeShopId: state => state.activeShopId,
            currentlySearching: state => state.currentlySearching
        };

        return {
            namespaced: true,
            state,
            mutations,
            actions,
            getters
        };
    });
}
